import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import { Helmet } from 'react-helmet';
import ButtonBase from '@mui/material/ButtonBase';
import AvatarMenu from './AvatarMenu';
import LoginModal from './LoginModal';
import PremiumPaywallModal from './PremiumPaywallModal';
import StoryLoadingOverlay from './StoryLoadingOverlay';
import WelcomeAppHero from './WelcomeAppHero';

// Founders.png loading animation is 17 frames @ 200ms; wait a full loop before navigating.
const START_READING_ANIMATION_MS = 3400;

export default function Welcome({
  onAbout = () => {},
  onLogin = () => {},
  onLogout = () => {},
  onAccount = () => {},
  user,
  authLoading = false,
  onNavigateHome,
  onGrammar = () => {},
  onBlog = () => {},
  onVideos = () => {},
  lastReadStory = null,
  onContinueReading = null,
  onOpenSeries = null,
  onOpenStory = null,
  newStories = [],
}) {
  // Always scroll to top on mount
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const [loginOpen, setLoginOpen] = React.useState(false);
  const [paywallOpen, setPaywallOpen] = React.useState(false);
  const [startReadingLoading, setStartReadingLoading] = React.useState(false);

  const handleStartReading = () => {
    setStartReadingLoading(true);
    setTimeout(onNavigateHome, START_READING_ANIMATION_MS);
  };
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState(null);

  const COURSES = [
    { level: 'HSK 1', title: 'Huangshan Adventures', icon: '/assets/Story Icons/huangshan.png', series: 'Huangshan', storyCount: 6 },
    { level: 'HSK 2', title: 'Summer Holiday', icon: '/assets/Story Icons/beach.webp', series: 'Summer_Holiday', storyCount: 10 },
    { level: 'HSK 3', title: 'Forest Girl Book One', icon: '/assets/Story Icons/Jungle_Girls.png', series: 'Forest_Girl_Book_1', storyCount: 3 },
  ];

  const YUNNAN_TILES = [
    { level: 'HSK 2', title: 'Kunming', icon: '/assets/Story Icons/Yunnan/yunnan-kunming.jpg', storyCount: 10 },
    { level: 'HSK 2', title: 'Xishuangbanna', icon: '/assets/Story Icons/Yunnan/yunnan-xishuangbanna.jpg', storyCount: 10 },
    { level: 'HSK 3', title: 'Dali', icon: '/assets/Story Icons/Yunnan/yunnan-dali.jpg', storyCount: 10 },
    { level: 'HSK 3', title: 'Shangrila', icon: '/assets/Story Icons/Yunnan/yunnan-shangrila.jpg', storyCount: 10 },
  ];

  const handleLoginClick = () => {
    setLoginOpen(true);
    setMobileMenuAnchor(null);
  };

  const handleCloseLogin = () => {
    setLoginOpen(false);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleAboutClick = () => {
    onAbout();
    handleMobileMenuClose();
  };

  return (
    <>
      {startReadingLoading && <StoryLoadingOverlay />}
      <Helmet>
        <title>Dumplingo | Learn Chinese Through Stories | Free HSK 1–6 Reading App</title>
        <meta name="description" content="Learn Mandarin through graded Chinese stories and connected reader courses that introduce new characters gradually. Native audio, karaoke highlighting, and instant definitions." />
        <meta name="keywords" content="learn Chinese, Mandarin reading app, HSK stories, graded readers Chinese, comprehensible input Chinese, free Chinese app, HSK 1 stories, HSK 2 stories, HSK 3 stories, learn Chinese characters, Chinese listening practice, Mandarin for beginners" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Dumplingo" />
        <link rel="canonical" href="https://dumplingo.net/" />

        {/* Open Graph */}
        <meta property="og:site_name" content="Dumplingo" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dumplingo.net/" />
        <meta property="og:title" content="Dumplingo — Learn Chinese Through Stories | Free HSK Reading App" />
        <meta property="og:description" content="Graded Mandarin stories and connected courses designed to stay readable as your vocabulary grows. Native audio, karaoke highlighting, and instant definitions." />
        <meta property="og:image" content="https://dumplingo.net/assets/og-cover.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@dumplingo" />
        <meta name="twitter:title" content="Dumplingo — Learn Chinese Through Stories" />
        <meta name="twitter:description" content="Graded Mandarin stories and connected courses that introduce new characters gradually, with native audio and instant definitions." />
        <meta name="twitter:image" content="https://dumplingo.net/assets/og-cover.jpg" />

        <link
          href="https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600&family=Space+Grotesk:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />

        {/* WebSite schema — enables Google Sitelinks Search Box */}
        <script type="application/ld+json">{`{
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Dumplingo",
  "alternateName": "Dumplingo Chinese",
  "url": "https://dumplingo.net/",
  "description": "Mandarin Chinese reading app with graded stories and connected courses that introduce new characters gradually, plus karaoke-synchronised audio and tap-for-definition.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://dumplingo.net/stories?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Dumplingo",
    "url": "https://dumplingo.net/",
    "logo": {
      "@type": "ImageObject",
      "url": "https://dumplingo.net/favicon_io/android-chrome-512x512.png",
      "width": 512,
      "height": 512
    },
    "sameAs": []
  }
}`}</script>

        {/* SoftwareApplication schema */}
        <script type="application/ld+json">{`{
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Dumplingo",
  "url": "https://dumplingo.net/",
  "description": "Mandarin Chinese graded reader with HSK-levelled stories, readable story courses, karaoke audio sync, instant word definitions, and progress tracking.",
  "applicationCategory": "EducationApplication",
  "operatingSystem": "Web, iOS, Android",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Selected stories and the first episode of each series are free. Premium is a monthly recurring subscription."
  },
  "featureList": [
    "Graded Mandarin stories across HSK 1–6",
    "Connected courses that introduce new characters gradually",
    "Karaoke-synchronised native audio",
    "Tap any word for instant definition and pinyin",
    "Offline-capable progressive web app",
    "HSK exam preparation content"
  ],
  "inLanguage": ["en", "zh"],
  "audience": {
    "@type": "Audience",
    "audienceType": "Mandarin Chinese learners, HSK exam students, language learners"
  }
}`}</script>

        {/* FAQ schema */}
        <script type="application/ld+json">{`{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Dumplingo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Dumplingo is a Mandarin Chinese reading app with graded stories across HSK levels 1–6. Connected reader courses introduce new characters gradually and repeat them in context, while karaoke-synchronised audio, tap-for-definition, and progress tracking support each story."
      }
    },
    {
      "@type": "Question",
      "name": "Is Dumplingo free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Selected stories and the first episode of every series are free to read. A Premium subscription (monthly recurring) unlocks every story and series episode."
      }
    },
    {
      "@type": "Question",
      "name": "What HSK levels does Dumplingo cover?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Dumplingo covers HSK 1 through HSK 6 with graded stories. Stories are organised by level, and connected courses introduce new characters gradually so learners can build vocabulary without losing readability."
      }
    },
    {
      "@type": "Question",
      "name": "How does the karaoke audio feature work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "When you play audio in Dumplingo, individual words in the story highlight in real time in sync with the speaker. This lets you follow along and associate spoken sounds with written characters, which greatly improves listening comprehension and tone recognition."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need to know Chinese characters to start?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Pinyin (romanised pronunciation) is shown above every character. You can tap any word to see its meaning and pinyin instantly. HSK 1 stories use very simple vocabulary so complete beginners can start right away without any prior knowledge."
      }
    },
    {
      "@type": "Question",
      "name": "How is Dumplingo different from Duolingo or other apps?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Dumplingo is focused entirely on reading comprehensible Chinese stories rather than gamified exercises or isolated vocabulary drills. Reading and listening to Chinese you understand — comprehensible input — is one of the most effective routes to fluency, especially beyond beginner level."
      }
    },
    {
      "@type": "Question",
      "name": "Can Dumplingo help me prepare for the HSK exam?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Every story on Dumplingo is tagged to a specific HSK level, so you can focus your reading practice on the vocabulary and grammar patterns relevant to your target exam. The grammar guides also cover key HSK grammar points for levels 1–3."
      }
    },
    {
      "@type": "Question",
      "name": "Does Dumplingo work on mobile?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Dumplingo works in any mobile browser on iOS and Android. You can also add it to your home screen as a progressive web app for an app-like experience without needing to visit an app store."
      }
    }
  ]
}`}</script>
      </Helmet>

      {/* Main Container - Prevent horizontal overflow */}
      <Box sx={{ width: '100%', overflowX: 'hidden', position: 'relative' }}>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            background: '#FACF9F'
          }}
        >
          <AppBar
            position="absolute"
            elevation={0}
            sx={{
              top: 0,
              background: 'transparent',
              boxShadow: 'none'
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                width: '100%',
                pl: { xs: 3, sm: 3, md: 3 },
                pr: { xs: 3, sm: 3, md: 3 },
                minHeight: { xs: 60, md: 70 },
                boxSizing: 'border-box'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 700,
                  letterSpacing: '.05em',
                  cursor: 'pointer',
                  fontSize: { xs: '26px', sm: '30px', md: '34px' },
                  lineHeight: 1,
                  color: '#2c2017',
                  ml: '-0.75rem'
                }}
              >
                <Box component="img" src={`${process.env.PUBLIC_URL || ''}/assets/dumplingo-icon.jpg`} alt="Dumplingo" sx={{ height: { xs: '2.5rem', sm: '3.2rem', md: '3.8rem' }, width: { xs: '2.5rem', sm: '3.2rem', md: '3.8rem' }, display: 'block', borderRadius: '22%', objectFit: 'cover' }} />
              </Typography>
              <Box sx={{ flexGrow: 1 }} />

              {/* Desktop Menu */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                <Button
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontSize: '1.9rem',
                    px: 2,
                    color: '#2c2017',
                    minWidth: 'auto'
                  }}
                  onClick={onNavigateHome}
                >
                  Stories
                </Button>
                <Button
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontSize: '1.9rem',
                    px: 2,
                    color: '#2c2017',
                    minWidth: 'auto'
                  }}
                  onClick={onAbout}
                >
                  About
                </Button>
                {authLoading ? (
                  <Box sx={{ width: 40, height: 40 }} />
                ) : user ? (
                  <AvatarMenu user={user} onAccount={onAccount} onLogout={onLogout} />
                ) : (
                  <Button
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: '"Space Grotesk", sans-serif',
                      fontSize: '1.9rem',
                      px: 2,
                      color: '#2c2017',
                      minWidth: 'auto',
                      '&:hover': { textDecoration: 'underline', background: 'transparent' }
                    }}
                    onClick={handleLoginClick}
                  >
                    My Study
                  </Button>
                )}
              </Box>

              {/* Mobile Menu */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
                {!authLoading && user && <AvatarMenu user={user} onAccount={onAccount} onLogout={onLogout} />}
                <IconButton
                  onClick={handleMobileMenuOpen}
                  sx={{ color: '#2c2017' }}
                  aria-label="menu"
                >
                  <MenuIcon sx={{ fontSize: 32 }} />
                </IconButton>
              </Box>

              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                PaperProps={{
                  sx: {
                    background: '#fff',
                    color: '#2c2017',
                    minWidth: 200
                  }
                }}
              >
                <MenuItem onClick={() => { onNavigateHome && onNavigateHome(); handleMobileMenuClose(); }}>
                  <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 }}>
                    Stories
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleAboutClick}>
                  <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 }}>
                    About
                  </Typography>
                </MenuItem>
                {!authLoading && !user && (
                  <MenuItem onClick={handleLoginClick}>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 }}>
                      My Study
                    </Typography>
                  </MenuItem>
                )}
              </Menu>
            </Toolbar>
          </AppBar>

          <WelcomeAppHero
            onStartReading={handleStartReading}
            onContinueReading={onContinueReading}
            lastReadStory={lastReadStory}
            user={user}
          />
        </Box>

        {/* ── Graded Reader Courses ─────────────────────── */}
        <Box
          component="section"
          sx={{
            background: '#FACF9F',
            color: '#2c2017',
            py: { xs: 7, md: 11 },
            px: { xs: 3, sm: 6, md: 10 },
          }}
        >
          <Box
            sx={{
              maxWidth: 1160,
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' },
              gap: { xs: 6, md: 10 },
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#B74825',
                  mb: 2,
                }}
              >
                Graded reader courses
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' },
                  lineHeight: 1.05,
                  color: '#2c2017',
                  mb: 3,
                  maxWidth: 520,
                }}
              >
                New characters. Stories that stay readable.
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Raleway", sans-serif',
                  fontSize: { xs: '1rem', md: '1.08rem' },
                  lineHeight: 1.75,
                  color: '#5a3e2b',
                  maxWidth: 540,
                  mb: 4,
                }}
              >
                Each course moves through different graded stories at a controlled pace. New characters arrive gradually, then reappear in context, so your vocabulary can grow without the next story becoming a wall of unknown text.
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto', columnGap: 4, borderTop: '1px solid rgba(44,32,23,0.24)' }}>
                {[
                  ['01', 'Meet them'],
                  ['02', 'Read them again'],
                  ['03', 'Make them yours'],
                ].map(([number, label]) => (
                  <Box key={number} sx={{ pt: 2 }}>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, color: '#B74825', fontSize: '0.78rem', mb: 0.5 }}>
                      {number}
                    </Typography>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, color: '#2c2017', fontSize: { xs: '0.78rem', sm: '0.9rem' }, lineHeight: 1.25 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 2 }}>
              {COURSES.map((course) => (
                <ButtonBase
                  key={course.series}
                  onClick={() => onOpenSeries && onOpenSeries(course.series)}
                  sx={{
                    width: '100%',
                    minHeight: { xs: 112, sm: 142 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '96px 1fr auto', sm: '132px 1fr auto' },
                    alignItems: 'center',
                    textAlign: 'left',
                    background: '#fff',
                    color: '#203B34',
                    overflow: 'hidden',
                    border: '1px solid rgba(44,32,23,0.14)',
                    transition: 'transform .2s ease, box-shadow .2s ease',
                    '&:hover': {
                      transform: 'translateX(6px)',
                      boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL || ''}${course.icon}`}
                    alt=""
                    sx={{ width: '100%', height: '100%', minHeight: { xs: 112, sm: 142 }, objectFit: 'cover' }}
                  />
                  <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#B74825', mb: 0.5 }}>
                      {course.level} course
                    </Typography>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: { xs: '1rem', sm: '1.3rem' }, lineHeight: 1.15, overflowWrap: 'anywhere' }}>
                      {course.title}
                    </Typography>
                    <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontWeight: 600, fontSize: '0.78rem', mt: 0.65, color: 'rgba(32,59,52,0.72)' }}>
                      {course.storyCount} graded stories
                    </Typography>
                  </Box>
                  <Typography aria-hidden="true" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 400, fontSize: { xs: '1.5rem', sm: '2rem' }, pr: { xs: 2, sm: 3 } }}>
                    →
                  </Typography>
                </ButtonBase>
              ))}
            </Box>
          </Box>

          {/* ── Discover Yunnan (coming soon) ─────────────── */}
          <Box sx={{ maxWidth: 1160, mx: 'auto', mt: { xs: 6, md: 9 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#B74825',
                }}
              >
                Discover Yunnan
              </Typography>
              <Box
                sx={{
                  background: '#2c2017',
                  color: '#FACF9F',
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 800,
                  fontSize: '0.62rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  px: 1,
                  py: 0.35,
                }}
              >
                Coming soon
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Raleway", sans-serif',
                fontSize: { xs: '0.95rem', md: '1rem' },
                lineHeight: 1.6,
                color: '#5a3e2b',
                maxWidth: 640,
                mb: 3,
              }}
            >
              A new travel course following one trip across four cities in Yunnan. In the works - not readable yet.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {YUNNAN_TILES.map((tile) => (
                <Box
                  key={tile.title}
                  sx={{
                    width: '100%',
                    minHeight: { xs: 112, sm: 142 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '96px 1fr', sm: '132px 1fr' },
                    alignItems: 'center',
                    textAlign: 'left',
                    background: '#fff',
                    color: '#203B34',
                    overflow: 'hidden',
                    border: '1px solid rgba(44,32,23,0.14)',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL || ''}${tile.icon}`}
                    alt=""
                    sx={{ width: '100%', height: '100%', minHeight: { xs: 112, sm: 142 }, objectFit: 'cover' }}
                  />
                  <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#B74825', mb: 0.5 }}>
                      {tile.level} course
                    </Typography>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: { xs: '1rem', sm: '1.3rem' }, lineHeight: 1.15, overflowWrap: 'anywhere' }}>
                      {tile.title}
                    </Typography>
                    <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontWeight: 600, fontSize: '0.78rem', mt: 0.65, color: 'rgba(32,59,52,0.72)' }}>
                      {tile.storyCount} graded stories · coming soon
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      background: '#2c2017',
                      color: '#FACF9F',
                      fontFamily: '"Space Grotesk", sans-serif',
                      fontWeight: 800,
                      fontSize: '0.6rem',
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      px: 1,
                      py: 0.3,
                    }}
                  >
                    Coming soon
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>


        {/* ── Features Section ─────────────────────────────── */}
        <Box
          component="section"
          sx={{
            background: '#fff',
            py: { xs: 6, md: 10 },
            px: { xs: 3, sm: 6, md: 10 },
            borderTop: '1px solid rgba(0,0,0,0.07)',
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 800,
              fontSize: { xs: '1.6rem', md: '2.2rem' },
              color: '#2c2017',
              textAlign: 'center',
              mb: 1,
            }}
          >
            Built to keep you inside the story
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '0.95rem', md: '1rem' },
              color: '#5a3e2b',
              textAlign: 'center',
              mb: { xs: 6, md: 9 },
              maxWidth: { xs: 520, md: 700 },
              mx: 'auto',
              whiteSpace: { md: 'nowrap' },
            }}
          >
            Less stopping to decode. More time reading Chinese you can follow.
          </Typography>

          {/* Staggered shapes row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              justifyContent: 'center',
              gap: { xs: 5, sm: 3, md: 6 },
              mx: 'auto',
            }}
          >
            {[
              {
                img: `${process.env.PUBLIC_URL || ''}/assets/feature-free.png`,
                title: 'Free to start',
                desc: 'Try selected stories and the first episode of each series before upgrading.',
                down: false,
              },
              {
                img: `${process.env.PUBLIC_URL || ''}/assets/feature-singing.png`,
                title: 'Karaoke audio',
                desc: 'Words highlight in real time as a native speaker reads aloud.',
                down: true,
              },
              {
                img: `${process.env.PUBLIC_URL || ''}/assets/feature-reading.png`,
                title: 'Tap any word',
                desc: 'Instant pinyin, definition, and full sentence translation in one tap.',
                down: false,
              },
              {
                img: `${process.env.PUBLIC_URL || ''}/assets/feature-hsk.png`,
                title: 'Graded readers',
                desc: 'Stories from HSK 1–6, including connected courses built for steady progression.',
                down: true,
                widthAuto: true,
                size: 141,
              },
            ].map(({ img, title, desc, down, size = 208, widthAuto = false }) => (
              <Box
                key={title}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  flex: 1,
                  maxWidth: { xs: 220, sm: 180, md: 200 },
                  mt: { xs: 0, sm: down ? '80px' : 0 },
                }}
              >
                {/* Icon — container always 208px tall so text aligns across all items */}
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', width: size, height: 208, overflow: 'visible' }}>
                  <Box component="img" src={img} alt={title} sx={{ height: size, width: widthAuto ? 'auto' : size, objectFit: 'contain' }} />
                </Box>
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#2c2017',
                    mb: 0.75,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontSize: '0.85rem',
                    color: '#5a3e2b',
                    lineHeight: 1.6,
                  }}
                >
                  {desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        

        {/* Login Modal */}
        <LoginModal open={loginOpen} onClose={handleCloseLogin} />

        <PremiumPaywallModal
          open={paywallOpen}
          onClose={() => setPaywallOpen(false)}
          user={user}
          onLogin={() => { setPaywallOpen(false); setLoginOpen(true); }}
          onUpgradeSuccess={() => setPaywallOpen(false)}
        />

        {/* App Preview Section - removed */}

      </Box>
    </>
  );
}